import { DUPE_API_URL } from '@dupe/common/constants'
import axios from 'axios'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'
import useSWR from 'swr'
import type { ResultsResp } from '~/src/db/queryResultsResp'
import useAuthUser from '~/src/hooks/useAuthUser'
import { idTokenForSend } from '~/src/lib/firebase/freshIdToken'
import useGetTempUserId from '~/src/hooks/useGetTempUserId'
import fetcher from '~/src/lib/fetcher'
import logger from '~/src/lib/logger'

/**
 * Delay between the first successful parse response and an automatic SWR
 * revalidation, so the FE picks up async-enriched secondary fields (colors,
 * sizes) populated by `enrich-parse-result-from-hash` Inngest job after the
 * controller responds.
 *
 * The Inngest job typically completes in 3-5s; revalidating at 5s gives the
 * job a comfortable window without waiting too long. The revalidation hits
 * the controller's `shouldRestoreFromDb` cache path (no re-scrape) so cost
 * is negligible. See docs/features/backlog/parse-async-enrichment/.
 */
const ASYNC_ENRICH_REVALIDATE_DELAY_MS = 5000

/**
 * Module-level dedup for the post-parse async-enrich revalidation. Several
 * components on a results page can mount `useGetParsedResults` concurrently
 * (`DupeResultsPage`, `ResultsEnhancedPageComponent`, `ChatsAdminControls`,
 * etc.) — each had its own `revalidationHashRef`, so they could each schedule
 * a +5s revalidation, beating SWR's 2s dedup window if they resolved at
 * different times. The 2026-05-05 westelm trace showed this manifesting as
 * back-to-back parse fires after the initial response.
 *
 * Set membership is "sticky" for the page lifetime — we only schedule the
 * revalidation once per hash globally, and never clear the entry. If the
 * user navigates between hashes the set grows by 1 each time, which is fine.
 * Page navigation / reload reset the set as expected.
 */
const scheduledRevalidations = new Set<string>()

const useGetParsedResults = ({
  hash,
  skip,
  source,
}: {
  hash: string | null | undefined
  skip?: boolean
  source?: string
}) => {
  const router = useRouter()
  const urlQuery = router.query?.url as string
  const { authUser, loading: isLoadingAuthUser } = useAuthUser()
  const { tempUserId } = useGetTempUserId()
  const [refreshing, setRefreshing] = useState(false)

  const loading = isLoadingAuthUser

  const endpoint = `${DUPE_API_URL}/api/parse`
  const shouldFetch = !skip && !loading && !!hash

  const { data, isValidating, isLoading, error, mutate } = useSWR<ResultsResp>(
    shouldFetch ? [endpoint, hash, urlQuery] : null,
    (
      [url, hashParam, urlQueryParam]: [string, string, string | undefined],
      { arg }: { arg?: { refresh?: boolean } } = {}
    ) => {
      return fetcher(url, {
        dataOrParams: {
          hash: hashParam,
          url: urlQueryParam,
          refresh: !!arg?.refresh,
          source,
        },
        authUser,
        tempUserId,
      })
    },
    {
      revalidateOnFocus: false,
      // errorRetryInterval: 1,
      // errorRetryCount: 2,
      // shouldRetryOnError: true,
      onErrorRetry: (error, key, _config, revalidate, { retryCount }) => {
        logger.warn(
          '[useGetParsedResults.onErrorRetry] Error getting parsed results',
          {
            key,
            hash,
            urlQuery,
            errorStatus: error?.status,
            errorMessage: error?.message,
            retryCount,
          }
        )

        // Never retry on 404.
        if (error?.status === 404) return

        // Never retry on 410 (circuit breaker — URL recently failed to parse).
        if (error?.status === 410) return

        // Never retry on 500.
        if (error?.status === 500) return

        // Retry up to 5 times.
        if (retryCount >= 5) return

        const exponentialBackoffWithJitter =
          Math.max(Math.pow(2, retryCount) * 1000, 10000) + Math.random() * 1000

        logger.warn(
          `[useGetParsedResults.onErrorRetry] Will retry in ${exponentialBackoffWithJitter}ms`,
          {
            key,
            hash,
          }
        )
        setTimeout(() => {
          revalidate({ retryCount })
        }, exponentialBackoffWithJitter)
      },
    }
  )

  // After the first successful parse for a given hash, schedule one
  // revalidation a few seconds later to pick up async-enriched fields
  // (colors / sizes) written by `enrich-parse-result-from-hash`.
  //
  // We only schedule when the response carries `enrichmentDispatched: true`
  // (i.e. this response actually fired the Inngest job). Cache hits and
  // failure paths return `enrichmentDispatched: false` — there's nothing
  // for the revalidation to pick up, and consuming the module-level dedup
  // slot on a cache hit would block a later force-refresh (which DOES
  // dispatch) from scheduling its own pickup.
  //
  // Dedup is module-level (`scheduledRevalidations`) so concurrent
  // `useGetParsedResults` mounts on the same page don't each fire their
  // own +5s timer. A per-instance ref still gates re-renders within this
  // hook, but the module set is what prevents cross-component concurrency.
  const revalidationHashRef = useRef<string | null>(null)
  const enrichmentDispatched =
    (data as { enrichmentDispatched?: boolean } | null | undefined)
      ?.enrichmentDispatched === true
  useEffect(() => {
    const responseHash = data?.hash
    if (!responseHash) return
    if (!enrichmentDispatched) return
    if (revalidationHashRef.current === responseHash) return
    revalidationHashRef.current = responseHash
    if (scheduledRevalidations.has(responseHash)) return
    scheduledRevalidations.add(responseHash)

    const id = setTimeout(() => {
      // SWR's `mutate()` with no arg revalidates against the current key
      // — refetches /api/parse, hits the DB-cache, returns enriched row
      // if the Inngest job has completed by now.
      mutate().catch(() => {
        // Non-fatal — the user already has the core data; failure here
        // just means a brief delay before secondary fields appear.
      })
    }, ASYNC_ENRICH_REVALIDATE_DELAY_MS)
    return () => clearTimeout(id)
  }, [data?.hash, enrichmentDispatched, mutate])

  const refresh = useCallback(
    async ({ refresh }: { refresh?: boolean } = {}) => {
      setRefreshing(true)
      // Force-refresh dispatches a fresh Inngest enrichment job at the
      // controller level. Drop the hash from the module-level dedup so the
      // post-response useEffect can schedule a *new* +5s pickup. Without this,
      // an in-session SPA navigation that returns to a previously-pickup'd
      // hash would skip the pickup even when this refresh dispatched again.
      if (refresh && hash) {
        scheduledRevalidations.delete(hash)
        if (revalidationHashRef.current === hash) {
          revalidationHashRef.current = null
        }
      }
      const resp = await mutate(
        () =>
          fetcher(endpoint, {
            dataOrParams: {
              hash,
              refresh,
              source,
            },
            authUser,
            tempUserId,
          }),
        false
      )
      setRefreshing(false)
      return resp
    },
    [endpoint, hash, authUser, tempUserId, mutate, source]
  )

  /** Row hash from the latest parse response; may differ from the route hash after variant refetch. */
  const effectiveResultHash = data?.hash ?? hash

  const updateFocusedImage = async (image: string) => {
    if (!effectiveResultHash) return
    axios.post(
      '/api/update-result',
      { hash: effectiveResultHash, image },
      {
        headers: {
          'x-authorization': await idTokenForSend(authUser),
          'x-temp-user-id': tempUserId,
        },
      }
    )
  }

  return {
    updateFocusedImage,
    refresh,
    endpoint,
    data,
    refreshing,
    isValidating,
    isLoading,
    error,
  }
}

export default useGetParsedResults
